<template>
  <div v-if="data" class="flow-page">
    <component :is="currentComponent" />

    <StyledReturnButton
      v-if="hasReturnUrl && !noDispayReturnButton.includes(currentComponent)"
      class="btn btn--med btn--transparent return-button"
      @click="handleReturn">
      {{ $t('return_to_shop') }}
    </StyledReturnButton>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import styled, { injectGlobal } from 'vue-styled-components';
import IframeEventService from '@/services/IframeEventService';
import MuchbetterPage from './FlowInvoice/MuchbetterPage/Index.vue';
import SelcomPage from './FlowInvoice/SelcomPage/Index.vue';
import KunaPage from './FlowInvoice/KunaPage/Index.vue';
import PiastrixPage from './FlowInvoice/PiastrixPage/Index.vue';
import PaywisePage from './FlowInvoice/PaywisePage/Index.vue';
import CrptPage from '@/pages/FlowInvoice/CrptPage/Index.vue';
import MultiInvoicePage from '@/pages/FlowInvoice/MultiInvoicePage/Index.vue';
import MifinityPage from '@/pages/FlowInvoice/MifinityPage/Index.vue';
import PaysensePage from '@/pages/FlowInvoice/PaysensePage/Index.vue';
import SpushPage from '@/pages/FlowInvoice/SpushPage/Index.vue';
import KlymePage from '@/pages/FlowInvoice/KlymePage/Index.vue';
import RapipagoPage from '@/pages/FlowInvoice/RapipagoPage/Index.vue';
import KlashaPage from '@/pages/FlowInvoice/KlashaPage/Index.vue';
import MtbPage from '@/pages/FlowInvoice/MtbPage/Index.vue';

const noDispayReturnButton = [
  'multi-invoice-page',
  'mifinity-page',
  'paysense-page',
  'spush-page',
  'rapipago-page',
];

const styledReturnButton = styled('button')`
  background: transparent;
  color: var(--primary) !important;
  box-shadow: var(--box_shadow);
  border-radius: var(--border_radius);
  border: 1px solid var(--primary);
  .overlay {
    z-index: 0;
  }
  &:hover {
    .overlay {
      opacity: 0.2;
    }
    box-shadow: var(--box_shadow_hover);
  }
  &:focus {
    .overlay {
      opacity: 0.4;
    }
  }
`;

const StyledMuchbetterPage = styled(MuchbetterPage)`
  color: var(--regular_text_color);
  border-color: var(--border_color);
`;

const StyledSelcomPage = styled(SelcomPage)`
  color: var(--regular_text_color);

  .collapse-container {
    border-bottom: 2px solid var(--border_color);

    &:first-child {
      border-top: 2px solid var(--border_color);
    }
  }
`;

const StyledPiastrixPage = styled(PiastrixPage)`
  color: var(--regular_text_color);
`;

const StyledKunaPage = styled(KunaPage)``;

const componentsMap = {
  muchbetter: 'styled-muchbetter-page',
  uax: 'styled-kuna-page',
  vodacom: 'styled-selcom-page',
  airtel_money: 'styled-selcom-page',
  tigo_pesa: 'styled-selcom-page',
  halo_pesa: 'styled-selcom-page',
  zantel_ezypesa: 'styled-selcom-page',
  paywise_cash: 'paywise-page',
  mts: 'styled-piastrix-page',
  megafon: 'styled-piastrix-page',
  beeline: 'styled-piastrix-page',
  tele2ru: 'styled-piastrix-page',
  bitcoin: 'crpt-page',
  bitcoin_cash: 'crpt-page',
  litecoin: 'crpt-page',
  tether: 'crpt-page',
  card_transfer: 'multi-invoice-page',
  upi_qr: 'multi-invoice-page',
  bank_transfer: 'multi-invoice-page',
  test: 'multi-invoice-page',
  pix_qr: 'multi-invoice-page',
  card_transfer_humo: 'multi-invoice-page',
  card_transfer_uzcard: 'multi-invoice-page',
  mifinity: 'mifinity-page',
  paysense: 'paysense-page',
  spush_pay: 'spush-page',
  rapipago: 'rapipago-page',
  klyme: 'klyme-page',
  klasha: 'klasha-page',
  mtb: 'mtb-page',
};

const getComponent = method_code => componentsMap[method_code] || 'multi-invoice-page';

export default {
  components: {
    styledReturnButton,
    StyledMuchbetterPage,
    StyledSelcomPage,
    StyledKunaPage,
    StyledPiastrixPage,
    CrptPage,
    PaywisePage,
    MultiInvoicePage,
    MifinityPage,
    PaysensePage,
    SpushPage,
    KlymePage,
    RapipagoPage,
    KlashaPage,
    MtbPage,
  },

  async created() {
    this.changeBackgroundColor();

    try {
      this.paymentInvoiceLoading = true;
      const { id } = this.paymentInvoice || false;
      this.requestRefreshLoading = true;

      if (!id) {
        throw new Error('No passed id');
      }

      clearInterval(this.checkStatusInterval);
      await this.getPaymentInvoiceById(id);

      /**
       * Set interval that will check status of payment
       */
      this.checkStatusInterval = setInterval(async () => await this.initInterval(), this.delay);
    } catch (e) {
      this.setGlobalError(e);
    } finally {
      this.requestRefreshLoading = false;
      this.paymentInvoiceLoading = false;
    }
  },

  data() {
    return {
      timeout: null,
      interval: null,
      noDispayReturnButton,
      checkStatusInterval: null,
      delay: 5000,
      // 295000 - 4 minutes 55 seconds
      // This number is chosen taking into account the delay, so that there is no extra request
      pullTime: 295000,
    };
  },

  computed: {
    ...mapState(['flowData', 'paymentInvoiceData', 'paymentInvoiceStatuses']),

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    state() {
      let currentStatusData = {};
      const currentStatus = this.paymentInvoice.status;
      if (currentStatus in this.paymentInvoiceStatuses) {
        currentStatusData = this.paymentInvoiceStatuses[currentStatus];
      } else {
        currentStatusData = this.paymentInvoiceStatuses['default'] || paymentStatusPending || {};
      }

      return currentStatusData;
    },

    hasReturnUrl() {
      return this.paymentInvoice.has_return_url;
    },

    currentComponent() {
      if (this.data.checkout_page) {
        return getComponent(this.data.checkout_page);
      }

      return getComponent(this.paymentInvoice.service_method);
    },

    data() {
      return this.flowData.payload;
    },
  },

  methods: {
    ...mapActions(['getPaymentInvoiceById', 'setGlobalError']),

    changeBackgroundColor() {
      let data = null;
      switch (this.currentComponent) {
        case 'multi-invoice-page':
        case 'rapipago-page':
          data = `
            .flow-page,
            body {
              background: #e5e5e5 !important;
            }
          `;

          break;
        case 'spush-page':
          data = `
            .flow-page {
              background: transparent !important;
            }

            body {
              background: linear-gradient(180deg, #F0F7FD 0%, #E1F1FF 100%) !important;

              &::before {
                position: absolute;
                width: 100%;
                height: 100%;
                content: '';
                background-image: url('data:image/svg+xml,<svg width="1303" height="651" viewBox="0 0 1303 651" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M899.835 651.483C899.835 789.522 788.287 901.419 650.691 901.419C513.096 901.419 401.547 789.522 401.547 651.483C401.547 513.444 513.096 401.547 650.691 401.547C788.287 401.547 899.835 513.444 899.835 651.483Z" stroke="white" stroke-width="2"/><path d="M1001.16 651.483C1001.16 844.602 844.251 1001.16 650.691 1001.16C457.131 1001.16 300.223 844.602 300.223 651.483C300.223 458.364 457.131 301.806 650.691 301.806C844.251 301.806 1001.16 458.364 1001.16 651.483Z" stroke="white" stroke-width="2"/><path d="M1100.9 651.483C1100.9 899.687 899.336 1100.9 650.691 1100.9C402.046 1100.9 200.482 899.687 200.482 651.483C200.482 403.278 402.046 202.065 650.691 202.065C899.336 202.065 1100.9 403.278 1100.9 651.483Z" stroke="white" stroke-width="2"/><path opacity="0.8" d="M1200.79 651.636C1200.79 955.803 954.572 1202.38 650.844 1202.38C347.116 1202.38 100.894 955.803 100.894 651.636C100.894 347.468 347.116 100.894 650.844 100.894C954.572 100.894 1200.79 347.468 1200.79 651.636Z" stroke="white" stroke-width="2"/><circle opacity="0.5" cx="651.483" cy="651.483" r="650.483" stroke="white" stroke-width="2"/></svg>');
                background-repeat: no-repeat;
                background-position: bottom;

                @media (max-width: 375px) {
                  background-size: 699px 349.5px;
                }
              }
            }
          `;
          break;
        default:
          data = null;
      }

      if (data) {
        injectGlobal`${data}`;
      }
    },

    async initInterval() {
      const { id } = this.paymentInvoice;
      if (this.pullTime <= 0) {
        this.delay = 15000;
        clearInterval(this.checkStatusInterval);
        this.checkStatusInterval = setInterval(async () => await this.initInterval(), this.delay);
      }
      if (this.state.isFinal) {
        clearInterval(this.checkStatusInterval);
        !this.hasReturnUrl &&
          this.$router.push({
            name: 'PaymentStatus',
            params: { id },
          });

        setTimeout(() => {
          this.redirectToMerchantSite();
        }, 3000);
      } else {
        try {
          await this.getPaymentInvoiceById(id);
        } catch (e) {}
      }

      if (this.pullTime > 0) {
        this.pullTime = this.pullTime - this.delay;
      }
    },

    handleReturn() {
      this.redirectToMerchantSite();
    },

    redirectToMerchantSite() {
      if (!this.hasReturnUrl) {
        return;
      }

      const returnUrl = `/return?id=${this.paymentInvoice.id}&force=true`;
      const fullReturnUrl = window.location.origin + returnUrl;

      IframeEventService.emitRedirect(fullReturnUrl);
      location.replace(returnUrl);
    },
  },
};
</script>

<style lang="scss" scoped>
.return-button {
  margin-top: 2em;
  border: 1px solid #2d4e77;
}
</style>
